import {useState} from "react";
import {ImagePlus, Plus, Trash2} from "lucide-react";
import {useTranslation} from "../../../shared/i18n";
import {ScheduleEditor} from "./ScheduleEditor";

const optionValue = (option) => String(option?.id ?? "");

const Field = ({label, name, value, pending, required = false, type = "text", multiline = false, onChange, ...props}) => {
    const Control = multiline ? "textarea" : "input";
    return (
        <label className={`branch-field${multiline ? " branch-field-wide" : ""}`}>
            <span>{label}{required ? " *" : ""}</span>
            <Control
                {...props}
                aria-label={label}
                type={multiline ? undefined : type}
                name={name}
                value={value ?? ""}
                required={required}
                disabled={pending}
                onChange={onChange}
            />
        </label>
    );
};

const SelectField = ({children, label, name, value, pending, required = false, onChange}) => (
    <label className="branch-field">
        <span>{label}{required ? " *" : ""}</span>
        <select
            aria-label={label}
            name={name}
            value={value ?? ""}
            required={required}
            disabled={pending}
            onChange={onChange}
        >
            {children}
        </select>
    </label>
);
const emptyStaff = () => ({
    name: "",
    phone_number: "",
    login: "",
    password: "",
    position: "",
    custom_reward_percent: "",
});

const StaffStaging = ({staff, positions, pending, onAdd, onRemove}) => {
    const {t} = useTranslation();
    const [draft, setDraft] = useState(emptyStaff);
    const [error, setError] = useState("");

    const updateDraft = (event) => {
        setError("");
        setDraft((current) => ({...current, [event.target.name]: event.target.value}));
    };

    const addStaff = () => {
        const required = ["name", "phone_number", "login", "password", "position"];
        if (required.some((field) => !String(draft[field] || "").trim())) {
            setError(t("orgs.branchForm.staffIncomplete"));
            return;
        }
        if (staff.some((item) => item.phone_number === draft.phone_number || item.login === draft.login)) {
            setError(t("orgs.branchForm.staffDuplicate"));
            return;
        }
        const percent = draft.custom_reward_percent;
        if (percent !== "" && (!Number.isFinite(Number(percent)) || Number(percent) < 0 || Number(percent) > 100)) {
            setError(t("orgs.branchForm.personalPercent"));
            return;
        }
        onAdd({...draft});
        setDraft(emptyStaff());
    };

    return (
        <div className="branch-staff-staging">
            <div className="branch-form-grid branch-staff-fields">
                <Field label={t("orgs.branchForm.staffName")} name="name" value={draft.name} pending={pending} onChange={updateDraft} />
                <Field label={t("orgs.branchForm.staffPhone")} name="phone_number" value={draft.phone_number} pending={pending} onChange={updateDraft} />
                <Field label={t("orgs.branchForm.staffLogin")} name="login" value={draft.login} pending={pending} autoComplete="off" onChange={updateDraft} />
                <Field label={t("orgs.branchForm.staffPassword")} name="password" value={draft.password} pending={pending} type="password" autoComplete="new-password" onChange={updateDraft} />
                <label className="branch-field">
                    <span>{t("orgs.branchForm.position")}</span>
                    <input
                        aria-label={t("orgs.branchForm.position")}
                        list="branch-position-options"
                        name="position"
                        value={draft.position}
                        disabled={pending}
                        onChange={updateDraft}
                    />
                    <datalist id="branch-position-options">
                        {positions.map((position) => <option key={optionValue(position)} value={position.name} />)}
                    </datalist>
                </label>
                <Field
                    label={t("orgs.branchForm.personalPercentLabel")}
                    name="custom_reward_percent"
                    value={draft.custom_reward_percent}
                    pending={pending}
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    onChange={updateDraft}
                />
            </div>
            {error ? <p className="branch-inline-error" role="alert">{error}</p> : null}
            <button className="admin-button admin-button-secondary" type="button" disabled={pending} onClick={addStaff}>
                <Plus aria-hidden="true" size={15} /> {t("orgs.branchForm.addStaff")}
            </button>
            {staff.length ? (
                <div className="branch-staged-list">
                    {staff.map((item, index) => (
                        <div className="branch-staged-row" key={`${item.login}-${item.phone_number}`}>
                            <div>
                                <strong>{item.name}</strong>
                                <span>{item.position} · {item.phone_number}</span>
                            </div>
                            <button
                                aria-label={t("orgs.branchForm.removeStaff", {name: item.name})}
                                title={t("orgs.branchForm.removeStaffTitle")}
                                type="button"
                                disabled={pending}
                                onClick={() => onRemove(index)}
                            >
                                <Trash2 aria-hidden="true" size={15} />
                            </button>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export const BranchForm = ({
    values,
    cities,
    categories,
    positions,
    galleryFiles,
    isCreate,
    pending,
    validationError,
    submitLabel,
    onChange,
    onGalleryChange,
    onAddStaff,
    onRemoveStaff,
    onSubmit,
}) => {
    const {t} = useTranslation();
    return (
    <form className="branch-form" onSubmit={onSubmit} noValidate>
        {validationError ? <div className="branch-form-error" role="alert">{validationError}</div> : null}

        <section className="branch-form-section" aria-labelledby="branch-profile-heading">
            <header>
                <h2 id="branch-profile-heading">{t("orgs.branchForm.profileTitle")}</h2>
                <p>{t("orgs.branchForm.profileCopy")}</p>
            </header>
            <div className="branch-form-grid">
                <Field label={t("orgs.branchForm.branchName")} name="name" value={values.name} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.branchForm.admin")} name="admin_name" value={values.admin_name} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.common.phone")} name="phone_number" value={values.phone_number} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.common.login")} name="login" value={values.login} pending={pending} required autoComplete="username" onChange={onChange} />
                {isCreate ? (
                    <Field label={t("orgs.common.password")} name="password" value={values.password} pending={pending} required type="password" autoComplete="new-password" onChange={onChange} />
                ) : null}
                <SelectField label={t("orgs.common.city")} name="city" value={values.city} pending={pending} required onChange={onChange}>
                    <option value="">{t("orgs.common.chooseCity")}</option>
                    {cities.filter((item) => optionValue(item) !== "0").map((item) => (
                        <option key={optionValue(item)} value={optionValue(item)}>{item.name}</option>
                    ))}
                </SelectField>
                <SelectField label={t("orgs.common.category")} name="category" value={values.category} pending={pending} required onChange={onChange}>
                    <option value="">{t("orgs.common.chooseCategory")}</option>
                    {categories.filter((item) => optionValue(item) !== "0").map((item) => (
                        <option key={optionValue(item)} value={optionValue(item)}>{item.name}</option>
                    ))}
                </SelectField>
            </div>
        </section>

        <section className="branch-form-section" aria-labelledby="branch-legal-heading">
            <header>
                <h2 id="branch-legal-heading">{t("orgs.branchForm.legalTitle")}</h2>
                <p>{t("orgs.branchForm.legalCopy")}</p>
            </header>
            <div className="branch-form-grid">
                <Field label={t("orgs.common.bin")} name="bin_number" value={values.bin_number} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.common.account")} name="account_number" value={values.account_number} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.branchForm.discount")} name="off_percent" value={values.off_percent} pending={pending} required type="number" min="0" max="100" step="1" onChange={onChange} />
                <Field label={t("orgs.branchForm.lat")} name="lat" value={values.lat} pending={pending} required type="number" step="any" onChange={onChange} />
                <Field label={t("orgs.branchForm.lng")} name="lng" value={values.lng} pending={pending} required type="number" step="any" onChange={onChange} />
                <Field label={t("orgs.branchForm.address")} name="address" value={values.address} pending={pending} required onChange={onChange} />
                <Field label={t("orgs.branchForm.description")} name="description" value={values.description} pending={pending} required multiline rows="4" onChange={onChange} />
            </div>
            <label className="branch-toggle">
                <input type="checkbox" name="is_shown" checked={values.is_shown} disabled={pending} onChange={onChange} />
                <span>{t("orgs.common.showInApp")}</span>
            </label>
        </section>

        <section className="branch-form-section" aria-labelledby="branch-social-heading">
            <header>
                <h2 id="branch-social-heading">{t("orgs.branchForm.socialTitle")}</h2>
                <p>{t("orgs.branchForm.socialCopy")}</p>
            </header>
            <div>
                <div className="branch-form-grid">
                    <Field label="Instagram" name="insta_link" value={values.insta_link} pending={pending} type="url" onChange={onChange} />
                    <Field label="WhatsApp" name="wp_link" value={values.wp_link} pending={pending} type="url" onChange={onChange} />
                    <Field label="2GIS" name="gis_link" value={values.gis_link} pending={pending} type="url" onChange={onChange} />
                    <Field label={t("orgs.branchForm.reward")} name="personal_reward_percent" value={values.personal_reward_percent} pending={pending} type="number" min="0" max="100" step="0.01" onChange={onChange} />
                    <Field label={t("orgs.branchForm.window")} name="staff_attribution_window_minutes" value={values.staff_attribution_window_minutes ?? ""} pending={pending} type="number" min="0" step="1" onChange={onChange} />
                </div>
                <label className="branch-toggle">
                    <input type="checkbox" name="personal_motivation_enabled" checked={values.personal_motivation_enabled} disabled={pending} onChange={onChange} />
                    <span>{t("orgs.branchForm.motivation")}</span>
                </label>
            </div>
        </section>

        <section className="branch-form-section" aria-labelledby="branch-schedule-heading">
            <header>
                <h2 id="branch-schedule-heading">{t("orgs.branchForm.scheduleTitle")}</h2>
                <p>{t("orgs.branchForm.scheduleCopy")}</p>
            </header>
            <ScheduleEditor values={values} pending={pending} onChange={onChange} />
        </section>

        <section className="branch-form-section" aria-labelledby="branch-staff-heading">
            <header>
                <h2 id="branch-staff-heading">{t("orgs.branchForm.staffTitle")}</h2>
                <p>{isCreate ? t("orgs.branchForm.staffCreateCopy") : t("orgs.branchForm.staffEditCopy")}</p>
            </header>
            {isCreate ? (
                <StaffStaging
                    staff={values.personal || []}
                    positions={positions}
                    pending={pending}
                    onAdd={onAddStaff}
                    onRemove={onRemoveStaff}
                />
            ) : (
                <p className="branch-section-note">{t("orgs.branchForm.staffCount", {count: (values.personal || []).length})}</p>
            )}
        </section>

        <section className="branch-form-section" aria-labelledby="branch-gallery-heading">
            <header>
                <h2 id="branch-gallery-heading">{t("orgs.branchForm.galleryTitle")}</h2>
                <p>{isCreate ? t("orgs.branchForm.galleryCreateCopy") : t("orgs.branchForm.galleryEditCopy")}</p>
            </header>
            {isCreate ? (
                <label className="branch-gallery-input">
                    <span>{t("orgs.branchForm.galleryPhotos")}</span>
                    <span className="branch-gallery-control">
                        <ImagePlus aria-hidden="true" size={17} />
                        {galleryFiles.length ? t("orgs.branchForm.galleryChosen", {count: galleryFiles.length}) : t("orgs.branchForm.galleryChoose")}
                    </span>
                    <input aria-label={t("orgs.branchForm.galleryPhotos")} type="file" accept="image/*" multiple disabled={pending} onChange={onGalleryChange} />
                </label>
            ) : (
                <p className="branch-section-note">{t("orgs.branchForm.galleryNote")}</p>
            )}
        </section>

        <footer className="branch-form-actions">
            <button className="admin-button admin-button-primary" type="submit" disabled={pending}>
                {pending ? t("orgs.common.saving") : submitLabel}
            </button>
        </footer>
    </form>
    );
};
